import { useState, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Building2, ChevronDown, ChevronRight } from "lucide-react";
import { useEntryDataStore } from "../../hooks/useDataEntryStore";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import { findOrgPathWithStatus } from "@/components/common/organization.utils";

const OrgGroup = observer(({ orgName, breadcrumb, orgId, children }) => {
  const [expanded, setExpanded] = useState(true);
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const { orgTree } = useEntryDataStore();
  const pathWithStatus = useMemo(
    () => findOrgPathWithStatus(orgTree, orgId),
    [orgTree, orgId],
  );

  return (
    <div className="py-2">
      <button
        type="button"
        className="flex items-center gap-2.5 w-full py-2 text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <Chevron size={14} className="shrink-0" />
        <Building2 size={16} className="text-muted-foreground shrink-0" />
        <div>
          <div className="flex items-center gap-1">
            <p className="font-semibold text-sm">{orgName}</p>
            {orgId && pathWithStatus.length > 0 && (
              <OrgStatusTag
                status={pathWithStatus[pathWithStatus.length - 1]?.status}
                inactiveDate={
                  pathWithStatus[pathWithStatus.length - 1]?.inactiveDate
                }
                className="text-[10px]"
              />
            )}
          </div>
          {(breadcrumb.length > 0 || orgName) && (
            <p className="text-xs text-muted-foreground">
              {pathWithStatus.length > 0 ? (
                <span className="inline-flex flex-wrap items-center gap-0.5">
                  {pathWithStatus.map((node, idx) => {
                    const isLast = idx === pathWithStatus.length - 1;
                    return (
                      <span
                        key={`${node.id}-${idx}`}
                        className="inline-flex items-center gap-0.5"
                      >
                        {idx > 0 && <span className="px-0.5">›</span>}
                        <span
                          className={
                            isLast ? "text-primary font-medium" : undefined
                          }
                        >
                          {node.name}
                        </span>
                        <OrgStatusTag
                          status={node.status}
                          inactiveDate={node.inactiveDate}
                          className="text-[10px]"
                        />
                      </span>
                    );
                  })}
                </span>
              ) : (
                <>
                  {breadcrumb.join(" › ")}
                  {breadcrumb.length > 0 && " › "}
                  <span className="text-primary font-medium">{orgName}</span>
                </>
              )}
            </p>
          )}
        </div>
      </button>

      {expanded && <div className="ml-8 space-y-3 mt-1">{children}</div>}
    </div>
  );
});

export default OrgGroup;
